import { useRef, useEffect } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab/Shift-Tab focus within `containerRef`.
 * When `active` becomes true, optionally auto-focuses the first focusable child.
 * @param {React.RefObject} containerRef
 * @param {boolean} active
 * @param {boolean} autoFocus  - focus first focusable element on activation (default true)
 */
export function useFocusTrap(containerRef, active = true, autoFocus = true) {
  useEffect(() => {
    if (!active) return;
    const el = containerRef.current;
    if (!el) return;

    const getFocusable = () => [...el.querySelectorAll(FOCUSABLE)];

    if (autoFocus) getFocusable()[0]?.focus();

    const handler = (e) => {
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [containerRef, active, autoFocus]);
}

/**
 * Captures `document.activeElement` at render time and restores it when the
 * component unmounts. Reliable even when the panel has an `autoFocus` child,
 * because the capture happens during the render phase before the DOM commit.
 */
export function useRestoreFocus() {
  const initialized = useRef(false);
  const triggerRef = useRef(null);
  if (!initialized.current && typeof document !== "undefined") {
    initialized.current = true;
    triggerRef.current = document.activeElement;
  }
  useEffect(
    () => () => {
      triggerRef.current?.focus({ preventScroll: true });
    },
    [],
  );
}
